import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardHead } from '../../design-system/composites/Card/Card';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';
import { PhoneIcon } from '@phosphor-icons/react/dist/csr/Phone';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';

import { LogCallDialog } from './LogCallDialog';
import { CALL_ACTION_LABEL_KEY, REQUEST_STATUS_LABEL_KEY } from './requestLabels';
import { kindLabel } from './kindLabel';
import { Button } from '../../design-system/primitives/Button/Button';
import { ApiError } from '../../lib/api/client';
import { requestsApi, type CaseRequestRow } from '../../lib/api/requests';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './RequestDetail.module.css';

/**
 * One request, in full — the surface `RequestScreen.tsx`'s own docblock has
 * reserved since M-09 ("should one ever be added"), and the thing the case
 * screen's Requests panel now opens when a row is clicked.
 *
 * ## Why a panel and not a URL
 *
 * `routing.ts` has no parameterised paths: every route in this application
 * is an exact literal (`/cases`, `/requests`), matched by `===` in
 * `App.tsx`. There is no `/requests/:uuid` to navigate to, and inventing a
 * parameterised scheme for one screen would leave the router with two ways
 * of deciding what is on screen. So this follows the shape `CaseDetail`
 * already established: a sibling panel with its own Back control, rendered
 * by the screen that owns the selection.
 *
 * The cost is honest and worth stating: a request detail cannot be
 * bookmarked or opened in a second tab, and a reload returns to the list.
 * Exactly the same is true of a case detail today.
 *
 * ## It fetches, and is never handed a row
 *
 * `props` carry a `uuid`, not a `CaseRequestRow`, even though both callers
 * are holding a perfectly good row when they open this. Two reasons, and
 * the second is the real one:
 *
 *  - the case panel's rows come from a `queue: 'all'` list load that may be
 *    minutes old by the time somebody clicks;
 *  - `next_action` decides which button this screen offers, and it is the
 *    server's answer to the state machine (`NextAction::for()` — never
 *    re-derived client-side, this module's Global Constraint). Acting on a
 *    stale one offers a resend that the server will answer 409, or hides a
 *    call that is now due.
 *
 * ## The actions are the queue's, not a second implementation
 *
 * Resend, complete and log-a-call are the same three `RequestList.tsx`
 * offers, gated on the same three permissions and driven by the same
 * `next_action` switch. They are repeated here rather than extracted
 * because extracting them would mean a shared component owning fetch state,
 * dialog state and row-merge callbacks for two very different layouts — the
 * duplication is four short handlers; the abstraction would be worse. What
 * is NOT duplicated is the decision: both read `next_action` and neither
 * computes it.
 */

export interface RequestDetailProps {
  uuid: string;
  permissions: readonly string[];
  onBack: () => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function RequestDetail({ uuid, permissions, onBack }: RequestDetailProps) {
  const { t, i18n } = useTranslation();

  const canResend = permissions.includes('request.resend');
  const canComplete = permissions.includes('request.complete');
  const canCall = permissions.includes('request.call');

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [record, setRecord] = useState<CaseRequestRow | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      setRecord(await requestsApi.get(uuid));
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [uuid]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(call: () => Promise<CaseRequestRow>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setActionFailed(false);

    try {
      setRecord(await call());
    } catch {
      setActionFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogged(): Promise<void> {
    setCallDialogOpen(false);

    try {
      // `POST .../calls` answers a standalone `CaseCallRow`, not the owning
      // request — so `next_action` and `calls` need a fresh read, never a
      // guess assembled from the call alone (`requests.ts`'s own docblock).
      setRecord(await requestsApi.get(uuid));
    } catch {
      // The call itself succeeded; only this follow-up read failed. Leaving
      // the panel stale is the same choice `RequestList` makes, and Retry
      // is on screen.
      setActionFailed(true);
    }
  }

  if (status === 'loading') {
    return (
      <section className={styles['panel']}>
        <p role="status">{t('list.loading')}</p>
      </section>
    );
  }

  if (status === 'error' || record === null) {
    return (
      <section className={styles['panel']} role="alert">
        <p className={styles['muted']}>{t('requests.detailLoadFailed')}</p>

        <div className={styles['actions']}>
          <Button variant="secondary" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
            {t('actions.back')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>
        </div>

        {reference === null ? null : (
          <p className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="requestDetailRequestId">
              {reference}
            </bdi>
          </p>
        )}
      </section>
    );
  }

  /** `null` renders the "not recorded" dash, never an empty cell. */
  function value(text: string | null): string {
    return text ?? t('table.notRecorded');
  }

  function time(iso: string | null): string {
    return iso === null ? t('table.notRecorded') : formatTimestamp(iso, i18n.language);
  }

  /**
   * THE ONE PLACE `next_action` DECIDES WHAT RENDERS — the identical switch
   * `RequestList.renderChaseAction` carries, over the same four closed
   * values. Nothing else on this screen is read to make the decision.
   */
  function chaseAction() {
    switch (record!.next_action) {
      case 'resend':
        return canResend ? (
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => {
              void act(() => requestsApi.resend(uuid));
            }}
            icon={<PaperPlaneTiltIcon size={16} />}
          >
            {t('requests.resend')}
          </Button>
        ) : null;
      case 'first_call':
      case 'second_call':
        return canCall ? (
          <Button
            variant="secondary"
            onClick={() => {
              setCallDialogOpen(true);
            }}
            icon={<PhoneIcon size={16} />}
          >
            {record!.next_action === 'first_call'
              ? t('requests.logFirstCall')
              : t('requests.logSecondCall')}
          </Button>
        ) : null;
      case 'none':
        return null;
    }
  }

  return (
    <section className={[styles['panel'], styles['primary']].join(' ')}>
      <div className={styles['header']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('actions.back')}
        </Button>

        <h2 className={styles['title']}>
          {kindLabel(record.kind, t) ?? t('requests.detailTitle')}
        </h2>

        <span className={styles['statusLabel']}>{t(REQUEST_STATUS_LABEL_KEY[record.status])}</span>
      </div>

      <dl className={styles['fields']}>
        <div className={styles['field']}>
          <dt>{t('table.caseNumber')}</dt>
          <dd>
            <bdi dir="ltr">{value(record.case?.case_number ?? null)}</bdi>
          </dd>
        </div>

        <div className={styles['field']}>
          <dt>{t('table.mobile')}</dt>
          <dd>
            <bdi dir="ltr">{value(record.mobile)}</bdi>
          </dd>
        </div>

        <div className={styles['field']}>
          <dt>{t('requests.kind')}</dt>
          <dd>{kindLabel(record.kind, t) ?? t('table.notRecorded')}</dd>
        </div>

        <div className={styles['field']}>
          <dt>{t('requests.askedAt')}</dt>
          <dd>
            <bdi dir="ltr">{time(record.asked_at)}</bdi>
          </dd>
        </div>

        <div className={styles['field']}>
          <dt>{t('requests.uploadedAt')}</dt>
          <dd>
            <bdi dir="ltr">{time(record.uploaded_at)}</bdi>
          </dd>
        </div>

        <div className={styles['field']}>
          <dt>{t('requests.resendLabel')}</dt>
          <dd>
            {record.resend.exists ? (
              <bdi dir="ltr">{time(record.resend.at)}</bdi>
            ) : (
              t('requests.resendNone')
            )}
          </dd>
        </div>
      </dl>

      <section className={styles['calls']}>
        <CardHead level="h3" icon={<PhoneIcon size={16} weight="duotone" />} title={t('requests.callsTitle')} />

        {record.calls.length === 0 ? (
          <p className={styles['muted']}>{t('requests.noCalls')}</p>
        ) : (
          <ul className={styles['callList']}>
            {record.calls.map((call) => (
              <li key={call.sequence} className={styles['call']}>
                <span className={styles['callHead']}>
                  {t('requests.callSummary', {
                    sequence: call.sequence,
                    action: call.action === null ? '' : t(CALL_ACTION_LABEL_KEY[call.action]),
                  })}
                </span>
                <span className={styles['callTime']}>
                  <bdi dir="ltr">{time(call.called_at)}</bdi>
                </span>
                {call.notes === null || call.notes === '' ? null : (
                  <p className={styles['callNotes']}>{call.notes}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
        Mounted EMPTY from the first frame rather than inserted on failure —
        a `role="alert"` added to the DOM at the moment it fills is announced
        inconsistently. The same shape every alert region here takes.
      */}
      <div className={styles['failure']} role="alert" aria-atomic="true">
        {/*
          `detailActionFailed`, NOT the queue's `actionFailed`. That string
          reads "The list below still shows the last state the server
          reported" — true on the queue, and a lie on a panel showing one
          request with no list under it.
        */}
        {actionFailed ? t('requests.detailActionFailed') : null}
      </div>

      <div className={styles['actions']}>
        {chaseAction()}

        {canComplete && record.status === 'open' ? (
          <Button
            variant="ghost"
            loading={busy}
            onClick={() => {
              void act(() => requestsApi.complete(uuid));
            }}
            icon={<CheckIcon size={16} />}
          >
            {t('requests.complete')}
          </Button>
        ) : null}
      </div>

      <LogCallDialog
        open={callDialogOpen}
        requestUuid={callDialogOpen ? uuid : null}
        onClose={() => {
          setCallDialogOpen(false);
        }}
        onLogged={() => {
          void handleLogged();
        }}
      />
    </section>
  );
}
